/*
  Product reviews: search, filters, sort, pages and the write-a-review form.
  Built 6 Oct 2026 to match paid Yotpo at Bayu (bayu-data-warehouse
  docs/reviews_app/21_yotpo_parity_reassessment.md s1.2 and s3).

  Every review of the product is already in the page: sections/product-reviews.liquid
  renders each one as a card (snippets/review-card.liquid) whose data-attributes
  carry what this file needs. This file NEVER fetches reviews. Its only network
  calls are the two form posts to the app proxy (contract with bayu-os, 6 Oct 2026):
    POST /apps/reviews/submit  {productId, rating, title, body, name, email, locale, website}
      -> {ok:true, status:"check_email", followUp?:"<opaque>"}
    POST /apps/reviews/brand   {rating, body, name, email, locale, website, followUp?}
      -> {ok:true, status:"check_email"}
      With the followUp from the submit answer, the one link in the first email
      confirms both reviews; without it the brand review gets its own email.
    Errors: {ok:false, error:"<code>", field?} - codes in ERROR_CODES below.
  The confirmation link lands on the product page with ?review=confirmed, or
  ?review=link_expired when the link is no longer valid.

  Two layers:
    1. Pure functions, no DOM - exported for tests/product-reviews.test.mjs.
    2. A thin DOM layer: read the cards once, keep one small state object,
       and redraw everything from it on every change.

  No framework, no dependencies, ES5 so it runs as-is in every browser the
  theme supports.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // Node: the tests
  } else {
    root.BayuProductReviews = api;
    api.start();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* 1. Pure functions                                                   */
  /* ------------------------------------------------------------------ */

  /*
    A review, as these functions see it:
      { index, rating, date, hasMedia, verified, topics, title, body }
    index     position in the page, used only to break ties so sorting is stable
    date      submitted_at as Unix seconds
    verified  'verified_buyer' | 'verified_reviewer' | '' - only what the card
              is allowed to SHOW (see snippets/review-card.liquid). A badge that
              is hidden for legal reasons must not quietly reorder the list either.
  */

  var SORTS = ['relevant', 'recent', 'media', 'verified', 'highest', 'lowest'];

  // Legal rule (doc 21 s5): the score is never part of the DEFAULT order.
  var DEFAULT_SORT = 'recent';

  // Yotpo's "smart topics" setting at Bayu: a topic needs at least 2 reviews.
  var TOPIC_MIN = 2;

  function bit(value) {
    return value ? 1 : 0;
  }

  function isVerified(review) {
    return review.verified === 'verified_buyer' || review.verified === 'verified_reviewer';
  }

  function byNewest(a, b) {
    return (b.date - a.date) || (a.index - b.index);
  }

  var COMPARE = {
    recent: byNewest,
    // "Most relevant" without the rating: media first, then verified, then newest.
    relevant: function (a, b) {
      return (bit(b.hasMedia) - bit(a.hasMedia)) ||
        (bit(isVerified(b)) - bit(isVerified(a))) ||
        byNewest(a, b);
    },
    media: function (a, b) {
      return (bit(b.hasMedia) - bit(a.hasMedia)) || byNewest(a, b);
    },
    // "Verified purchase" means a purchase: Verified Buyer only, not Verified Reviewer.
    verified: function (a, b) {
      return (bit(b.verified === 'verified_buyer') - bit(a.verified === 'verified_buyer')) ||
        byNewest(a, b);
    },
    highest: function (a, b) {
      return (b.rating - a.rating) || byNewest(a, b);
    },
    lowest: function (a, b) {
      return (a.rating - b.rating) || byNewest(a, b);
    }
  };

  function sortReviews(reviews, sortKey) {
    var compare = COMPARE[sortKey] || COMPARE[DEFAULT_SORT];
    return reviews.slice().sort(compare);
  }

  function lower(text) {
    return String(text == null ? '' : text).toLowerCase();
  }

  // Search matches the headline and the body, ignoring case.
  function matchesSearch(review, query) {
    var q = lower(query).trim();
    if (!q) return true;
    return lower(review.title + ' ' + review.body).indexOf(q) !== -1;
  }

  /*
    criteria: { query, rating, withMedia, topic }
      rating 0 (or missing) = every rating; topic '' (or missing) = every topic.
    All criteria must hold.
  */
  function filterReviews(reviews, criteria) {
    var c = criteria || {};
    return reviews.filter(function (review) {
      if (c.rating && review.rating !== c.rating) return false;
      if (c.withMedia && !review.hasMedia) return false;
      if (c.topic && review.topics.indexOf(c.topic) === -1) return false;
      return matchesSearch(review, c.query);
    });
  }

  // The topics worth offering as a filter, in vocabulary order.
  function topicsToShow(reviews, vocabulary, min) {
    var threshold = min || TOPIC_MIN;
    return vocabulary.filter(function (topic) {
      var n = 0;
      reviews.forEach(function (review) {
        if (review.topics.indexOf(topic) !== -1) n += 1;
      });
      return n >= threshold;
    });
  }

  function pageCount(total, pageSize) {
    return Math.max(1, Math.ceil(total / pageSize));
  }

  // Desktop: numbered pages. `page` is clamped into range.
  function paginate(reviews, page, pageSize) {
    var pages = pageCount(reviews.length, pageSize);
    var current = Math.min(Math.max(1, page || 1), pages);
    var start = (current - 1) * pageSize;
    return { items: reviews.slice(start, start + pageSize), page: current, pages: pages };
  }

  // Mobile: the first `shown` reviews, plus whether "Load more reviews" still has work.
  function loadMore(reviews, shown) {
    return { items: reviews.slice(0, shown), hasMore: reviews.length > shown };
  }

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // '2025-09-16' or '2025-09-16T06:54:23Z' -> 'September 16, 2025'. Pure string work, so no
  // time zone can move the day; Liquid already wrote the date in the shop's zone.
  function formatDate(isoDate) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ''));
    if (!m) return '';
    var monthIndex = parseInt(m[2], 10) - 1;
    var day = parseInt(m[3], 10);
    var year = m[1];
    return MONTHS[monthIndex] + ' ' + day + ', ' + year;
  }

  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var LIMITS = { title: 150, body: 5000, name: 60 };

  function trimmed(value) {
    return String(value == null ? '' : value).trim();
  }

  function validRating(value) {
    var n = Number(value);
    return n >= 1 && n <= 5 && Math.floor(n) === n;
  }

  /*
    Returns { field: localeKey } for every problem; an empty object means valid.
    The keys are products.review_form.* locale keys.
  */
  function validateReview(values) {
    var errors = {};
    if (!validRating(values.rating)) errors.rating = 'error_rating';
    if (trimmed(values.title).length > LIMITS.title) errors.title = 'error_headline_long';
    var body = trimmed(values.body);
    if (!body) errors.body = 'error_body';
    else if (body.length > LIMITS.body) errors.body = 'error_body_long';
    var name = trimmed(values.name);
    if (!name) errors.name = 'error_name';
    else if (name.length > LIMITS.name) errors.name = 'error_name_long';
    if (!EMAIL.test(trimmed(values.email))) errors.email = 'error_email';
    return errors;
  }

  // The brand follow-up: a rating is the only required thing.
  function validateBrand(values) {
    var errors = {};
    if (!validRating(values.rating)) errors.rating = 'error_rating';
    if (trimmed(values.body).length > LIMITS.body) errors.body = 'error_body_long';
    return errors;
  }

  // Error codes the review service sends -> products.review_form.* locale keys.
  // The server's own words are never shown; an unknown code gets the generic line.
  var ERROR_CODES = {
    rating_required: 'error_rating',
    body_required: 'error_body',
    name_required: 'error_name',
    email_invalid: 'error_email',
    product_unknown: 'error_product_unknown',
    too_long: 'error_too_long',
    rate_limited: 'error_rate_limited',
    server_error: 'error_generic'
  };

  // too_long on a field we know gets that field's own limit in the message.
  var TOO_LONG = { title: 'error_headline_long', body: 'error_body_long', name: 'error_name_long' };

  /*
    Turns an app-proxy answer into what the form should do.
      { kind: 'ok', followUp }     - followUp is '' when the server sent none
      { kind: 'field', field, key } - show locale key `key` next to that field
      { kind: 'form', key }         - show locale key `key` above the button
    No usable answer at all (unreadable body) is a 'form' error_generic.
  */
  function interpretResponse(data, knownFields) {
    if (data && data.ok === true) {
      return { kind: 'ok', followUp: typeof data.followUp === 'string' ? data.followUp : '' };
    }
    var code = data && data.ok === false ? data.error : null;
    var key = Object.prototype.hasOwnProperty.call(ERROR_CODES, code) ? ERROR_CODES[code] : 'error_generic';
    var field = data && data.field;
    var onField = !!field && (knownFields || []).indexOf(field) !== -1;
    if (code === 'too_long' && onField && TOO_LONG[field]) key = TOO_LONG[field];
    if (onField && key !== 'error_generic') return { kind: 'field', field: field, key: key };
    return { kind: 'form', key: key };
  }

  // ?review=confirmed / ?review=link_expired -> which notice to show ('' = none).
  var NOTICES = ['confirmed', 'link_expired'];
  function reviewNotice(search) {
    // Both accepted values are plain words, so no decoding is needed - and a
    // malformed escape in someone's URL cannot throw here.
    var match = /[?&]review=([^&#]*)/.exec(String(search || ''));
    var value = match ? match[1] : '';
    return NOTICES.indexOf(value) !== -1 ? value : '';
  }

  // ?review=write -> open the form on arrival. The link in the review-request
  // email (Klaviyo) uses it, so the shopper lands on an open form, as with Yotpo.
  function wantsForm(search) {
    return /[?&]review=write(?:&|#|$)/.test(String(search || ''));
  }

  /* ------------------------------------------------------------------ */
  /* 2. DOM layer                                                        */
  /* ------------------------------------------------------------------ */

  var DESKTOP = '(min-width: 750px)';
  var REVIEW_FIELDS = ['rating', 'title', 'body', 'name', 'email'];

  function readReview(card, index) {
    var title = card.querySelector('[data-review-title]');
    var body = card.querySelector('[data-review-body]');
    return {
      el: card,
      index: index,
      rating: parseInt(card.getAttribute('data-rating'), 10) || 0,
      date: Number(card.getAttribute('data-date')) || 0,
      hasMedia: card.getAttribute('data-media') === 'true',
      verified: card.getAttribute('data-verified') || '',
      topics: (card.getAttribute('data-topics') || '').split(/\s+/).filter(Boolean),
      title: title ? title.textContent : '',
      body: body ? body.textContent : ''
    };
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    }).then(function (response) {
      // A 4xx carries {ok:false,...}; an unreadable body becomes null -> error_generic.
      return response.json().catch(function () { return null; });
    });
  }

  function formValues(form) {
    var values = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.type === 'file') return;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      values[el.name] = el.value;
    });
    return values;
  }

  function Reviews(root) {
    this.root = root;
    this.strings = JSON.parse(root.querySelector('[data-pr-strings]').textContent);
    this.perPage = parseInt(root.getAttribute('data-per-page'), 10) || 5;
    this.mq = window.matchMedia(DESKTOP);
    this.list = root.querySelector('[data-pr-list]');
    this.reviews = this.list
      ? Array.prototype.map.call(this.list.querySelectorAll('[data-review]'), readReview)
      : [];
    this.state = { query: '', rating: 0, withMedia: false, topic: '', sort: DEFAULT_SORT, page: 1, shown: this.perPage };
    this.initNotice();
    this.initForm();
    if (this.list) this.initList();
  }

  Reviews.prototype.q = function (selector) {
    return this.root.querySelector(selector);
  };

  Reviews.prototype.qa = function (selector) {
    return Array.prototype.slice.call(this.root.querySelectorAll(selector));
  };

  // The shopper followed the link in the confirmation email.
  Reviews.prototype.initNotice = function () {
    var which = reviewNotice(window.location.search);
    this.qa('[data-pr-notice]').forEach(function (notice) {
      notice.hidden = notice.getAttribute('data-pr-notice') !== which;
    });
  };

  /* ---- the list: search, filters, sort, pages ---- */

  Reviews.prototype.initList = function () {
    var self = this;

    // From here on this script decides what is visible; the no-JS CSS rule stops.
    this.list.classList.add('is-js');

    this.qa('[data-review-date]').forEach(function (time) {
      time.textContent = formatDate(time.getAttribute('datetime'));
    });

    if (this.reviews.length > 1) {
      this.qa('[data-pr-controls]').forEach(function (el) { el.hidden = false; });
    }
    var count = this.q('[data-pr-count]');
    if (count) count.hidden = false;

    // Topic chips: rendered for every topic in the vocabulary, shown here only
    // when at least TOPIC_MIN reviews carry them.
    var chips = this.qa('[data-pr-topic]');
    var shownTopics = topicsToShow(this.reviews, chips.map(function (c) { return c.getAttribute('data-pr-topic'); }));
    chips.forEach(function (chip) {
      chip.hidden = shownTopics.indexOf(chip.getAttribute('data-pr-topic')) === -1;
    });
    var topicGroup = this.q('[data-pr-topics]');
    if (topicGroup) topicGroup.hidden = shownTopics.length === 0;

    var search = this.q('[data-pr-search]');
    var timer = null;
    if (search) {
      search.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { self.update({ query: search.value }); }, 150);
      });
    }

    var ratingSelect = this.q('[data-pr-rating]');
    if (ratingSelect) {
      ratingSelect.addEventListener('change', function () {
        self.update({ rating: parseInt(ratingSelect.value, 10) || 0 });
      });
    }

    // A distribution bar filters to its star; pressing the active bar clears it.
    this.qa('[data-pr-bar]').forEach(function (bar) {
      bar.addEventListener('click', function () {
        var star = parseInt(bar.getAttribute('data-pr-bar'), 10);
        self.update({ rating: self.state.rating === star ? 0 : star });
      });
    });

    var media = this.q('[data-pr-media]');
    if (media) {
      media.addEventListener('change', function () { self.update({ withMedia: media.checked }); });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var topic = chip.getAttribute('data-pr-topic');
        self.update({ topic: self.state.topic === topic ? '' : topic });
      });
    });

    var sort = this.q('[data-pr-sort]');
    if (sort) {
      sort.value = DEFAULT_SORT;
      sort.addEventListener('change', function () { self.update({ sort: sort.value }); });
    }

    this.qa('[data-pr-clear]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (search) search.value = '';
        if (media) media.checked = false;
        self.update({ query: '', rating: 0, withMedia: false, topic: '' });
      });
    });

    var more = this.q('[data-pr-more]');
    if (more) {
      more.addEventListener('click', function () {
        self.state.shown += self.perPage;
        self.render();
      });
    }

    var pages = this.q('[data-pr-pages]');
    if (pages) {
      pages.addEventListener('click', function (e) {
        var button = e.target.closest('[data-pr-page]');
        if (!button || button.disabled) return;
        self.state.page = parseInt(button.getAttribute('data-pr-page'), 10);
        self.render();
        var current = pages.querySelector('[aria-current="page"]');
        if (current) current.focus();
        if (self.list.getBoundingClientRect().top < 0) {
          self.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    this.qa('[data-review-more]').forEach(function (button) {
      button.addEventListener('click', function () {
        var body = button.parentNode.querySelector('[data-review-body]');
        var open = button.getAttribute('aria-expanded') !== 'true';
        body.classList.toggle('is-clamped', !open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        button.textContent = open ? self.strings.readLess : self.strings.readMore;
      });
    });
    this.qa('[data-review-body]').forEach(function (body) { body.classList.add('is-clamped'); });

    var onViewport = function () { self.render(); };
    if (this.mq.addEventListener) this.mq.addEventListener('change', onViewport);
    else if (this.mq.addListener) this.mq.addListener(onViewport);

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { self.updateReadMore(); }, 200);
    });

    this.render();
  };

  // Any filter, search or sort change starts again from the first page.
  Reviews.prototype.update = function (change) {
    for (var key in change) {
      if (Object.prototype.hasOwnProperty.call(change, key)) this.state[key] = change[key];
    }
    this.state.page = 1;
    this.state.shown = this.perPage;
    this.render();
  };

  Reviews.prototype.render = function () {
    var self = this;
    var state = this.state;
    var matching = sortReviews(filterReviews(this.reviews, state), state.sort);
    var view = paginate(matching, state.page, this.perPage);
    state.page = view.page;

    // Matching reviews first, in sorted order; the rest follow, hidden.
    var visible = view.items;
    matching.forEach(function (review) { self.list.appendChild(review.el); });
    this.reviews.forEach(function (review) {
      var show = visible.indexOf(review) !== -1;
      review.el.hidden = !show;
      if (matching.indexOf(review) === -1) self.list.appendChild(review.el);
    });

    var count = this.q('[data-pr-count]');
    if (count) count.textContent = this.strings.results[matching.length] || String(matching.length);

    var empty = this.q('[data-pr-empty]');
    if (empty) empty.hidden = matching.length !== 0;

    var ratingSelect = this.q('[data-pr-rating]');
    if (ratingSelect) ratingSelect.value = String(state.rating);
    this.qa('[data-pr-bar]').forEach(function (bar) {
      bar.setAttribute('aria-pressed', parseInt(bar.getAttribute('data-pr-bar'), 10) === state.rating ? 'true' : 'false');
    });
    this.qa('[data-pr-topic]').forEach(function (chip) {
      chip.setAttribute('aria-pressed', chip.getAttribute('data-pr-topic') === state.topic ? 'true' : 'false');
    });
    var sort = this.q('[data-pr-sort]');
    if (sort) sort.value = state.sort;

    var summary = this.q('[data-pr-pagination-summary]');
    if (summary) {
      if (matching.length === 0) {
        summary.textContent = '';
      } else {
        var start = (view.page - 1) * this.perPage + 1;
        var end = Math.min(view.page * this.perPage, matching.length);
        summary.textContent = 'Showing ' + start + '–' + end + ' of ' + matching.length + ' reviews';
      }
    }

    var more = this.q('[data-pr-more]');
    if (more) more.hidden = true;

    this.renderPages(view);
    this.updateReadMore();
  };

  // Numbered pages: previous, 1..n with smart ellipsis, next.
  Reviews.prototype.renderPages = function (view) {
    var nav = this.q('[data-pr-pages]');
    if (!nav) return;
    nav.innerHTML = '';
    nav.hidden = !view || view.pages <= 1;
    if (nav.hidden) return;

    var s = this.strings;
    var current = view.page;
    var total = view.pages;

    var add = function (page, text, label, disabled, isCurrent) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-reviews__page';
      button.setAttribute('data-pr-page', String(page));
      button.setAttribute('aria-label', label);
      button.textContent = text;
      button.disabled = disabled;
      if (isCurrent) button.setAttribute('aria-current', 'page');
      nav.appendChild(button);
    };

    var addEllipsis = function () {
      var span = document.createElement('span');
      span.className = 'product-reviews__ellipsis';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '...';
      nav.appendChild(span);
    };

    // Previous arrow button
    var prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'product-reviews__page-nav product-reviews__page-nav--prev';
    prev.setAttribute('data-pr-page', String(current - 1));
    prev.setAttribute('aria-label', s.previous || 'Previous page');
    prev.disabled = current === 1;
    prev.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-left" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    nav.appendChild(prev);

    // Page range logic
    var pagesToShow = [];
    if (total <= 5) {
      for (var i = 1; i <= total; i++) pagesToShow.push(i);
    } else if (current <= 3) {
      pagesToShow = [1, 2, 3, '...', total];
    } else if (current >= total - 2) {
      pagesToShow = [1, '...', total - 2, total - 1, total];
    } else {
      pagesToShow = [1, '...', current - 1, current, current + 1, '...', total];
    }

    for (var j = 0; j < pagesToShow.length; j++) {
      var item = pagesToShow[j];
      if (item === '...') {
        addEllipsis();
      } else {
        add(item, String(item), (s.page || 'Page __PAGE__').replace('__PAGE__', String(item)), false, item === current);
      }
    }

    // Next arrow button
    var next = document.createElement('button');
    next.type = 'button';
    next.className = 'product-reviews__page-nav product-reviews__page-nav--next';
    next.setAttribute('data-pr-page', String(current + 1));
    next.setAttribute('aria-label', s.next || 'Next page');
    next.disabled = current === total;
    next.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-right" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    nav.appendChild(next);
  };

  // "Read more" only where the clamped text really is cut off. Measured on the
  // visible cards, because a hidden card has no height to measure.
  Reviews.prototype.updateReadMore = function () {
    this.reviews.forEach(function (review) {
      if (review.el.hidden) return;
      var body = review.el.querySelector('[data-review-body]');
      var button = review.el.querySelector('[data-review-more]');
      if (!body || !button) return;
      if (button.getAttribute('aria-expanded') === 'true') {
        button.hidden = false;
        return;
      }
      button.hidden = body.scrollHeight <= body.clientHeight + 1;
    });
  };

  /* ---- the write-a-review form ---- */

  Reviews.prototype.initForm = function () {
    var self = this;
    var toggle = this.q('[data-pr-write-toggle]');
    var wrap = this.q('[data-pr-form-wrap]');
    var form = this.q('[data-pr-form]');
    if (!wrap || !form) return;

    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', function () {
        var open = wrap.hidden;
        wrap.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          var first = wrap.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
          if (first) first.focus();
        }
      });
    }

    this.qa('[data-pr-stars]').forEach(function (row) { self.initStars(row); });

    if (wantsForm(window.location.search)) {
      wrap.hidden = false;
      this.root.scrollIntoView();
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.submitReview(form);
    });

    var brand = this.q('[data-pr-brand]');
    if (brand) {
      brand.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submitBrand(brand);
      });
      var skip = this.q('[data-pr-brand-skip]');
      if (skip) {
        skip.addEventListener('click', function () {
          brand.hidden = true;
          var message = self.q('[data-pr-thanks-message]');
          if (message) message.focus();
        });
      }
    }
  };

  // Star picker: radios do the work (keyboard, screen readers, no-JS); this only
  // colours the stars and shows the Very poor ... Great! label.
  Reviews.prototype.initStars = function (row) {
    var labels = this.strings.stars;
    var text = row.querySelector('[data-pr-star-text]');
    var show = function (value) {
      row.setAttribute('data-shown', value || '');
      if (text) text.textContent = value ? labels[value - 1] : '';
    };
    var checked = function () {
      var input = row.querySelector('input:checked');
      return input ? parseInt(input.value, 10) : 0;
    };
    row.addEventListener('change', function () { show(checked()); });
    Array.prototype.forEach.call(row.querySelectorAll('label'), function (label) {
      var input = label.querySelector('input');
      label.addEventListener('mouseenter', function () { show(parseInt(input.value, 10)); });
    });
    row.addEventListener('mouseleave', function () { show(checked()); });
  };

  Reviews.prototype.clearErrors = function (form) {
    Array.prototype.forEach.call(form.querySelectorAll('[data-pr-error]'), function (el) {
      el.textContent = '';
      el.hidden = true;
    });
    Array.prototype.forEach.call(form.querySelectorAll('[aria-invalid]'), function (el) {
      el.removeAttribute('aria-invalid');
    });
  };

  Reviews.prototype.showError = function (form, field, message) {
    var target = form.querySelector('[data-pr-error="' + field + '"]') ||
      form.querySelector('[data-pr-error="form"]');
    target.textContent = message;
    target.hidden = false;
    var input = form.querySelector('[name="' + field + '"]');
    if (input) input.setAttribute('aria-invalid', 'true');
    return input;
  };

  Reviews.prototype.showErrors = function (form, errors) {
    var self = this;
    var first = null;
    Object.keys(errors).forEach(function (field) {
      var input = self.showError(form, field, self.strings.errors[errors[field]]);
      if (!first) first = input;
    });
    if (first) first.focus();
  };

  Reviews.prototype.busy = function (form, sending) {
    var button = form.querySelector('[type="submit"]');
    if (!button) return;
    if (sending) {
      button.setAttribute('data-label', button.textContent);
      button.textContent = this.strings.sending;
    } else if (button.getAttribute('data-label')) {
      button.textContent = button.getAttribute('data-label');
    }
    button.disabled = sending;
  };

  // Shared by both forms: post, then route the answer to the right place.
  Reviews.prototype.send = function (form, url, payload, onOk) {
    var self = this;
    this.busy(form, true);
    post(url, payload).then(function (data) {
      self.busy(form, false);
      var result = interpretResponse(data, REVIEW_FIELDS);
      if (result.kind === 'ok') return onOk(result);
      var message = self.strings.errors[result.key] || self.strings.errors.error_generic;
      if (result.kind === 'field') {
        var input = self.showError(form, result.field, message);
        if (input) input.focus();
        return;
      }
      self.showError(form, 'form', message);
    }, function () {
      self.busy(form, false);
      self.showError(form, 'form', self.strings.errors.error_network);
    });
  };

  Reviews.prototype.submitReview = function (form) {
    var self = this;
    var values = formValues(form);
    this.clearErrors(form);
    var errors = validateReview(values);
    if (Object.keys(errors).length) return this.showErrors(form, errors);

    // Kept for the brand follow-up, which goes out under the same name and email
    // (and, once the server answers, with its followUp token).
    this.author = { name: trimmed(values.name), email: trimmed(values.email), website: values.website || '' };
    this.send(form, '/apps/reviews/submit', {
      productId: Number(this.root.getAttribute('data-product-id')),
      rating: Number(values.rating),
      title: trimmed(values.title),
      body: trimmed(values.body),
      name: this.author.name,
      email: this.author.email,
      locale: this.root.getAttribute('data-locale'),
      website: this.author.website
    }, function (result) {
      self.followUp = result.followUp;
      form.hidden = true;
      var thanks = self.q('[data-pr-thanks]');
      thanks.hidden = false;
      var message = self.q('[data-pr-thanks-message]');
      if (message) message.focus();
    });
  };

  Reviews.prototype.submitBrand = function (brand) {
    var self = this;
    var values = formValues(brand);
    var author = this.author || {};
    this.clearErrors(brand);
    var errors = validateBrand(values);
    if (Object.keys(errors).length) return this.showErrors(brand, errors);
    var payload = {
      rating: Number(values.rating),
      body: trimmed(values.body),
      name: author.name || '',
      email: author.email || '',
      locale: this.root.getAttribute('data-locale'),
      website: author.website || ''
    };
    // Sent only when the server gave one; without it the brand review is a
    // standalone one and gets its own confirmation email.
    if (this.followUp) payload.followUp = this.followUp;
    this.send(brand, '/apps/reviews/brand', payload, function () {
      brand.hidden = true;
      var done = self.q(payload.followUp ? '[data-pr-brand-thanks]' : '[data-pr-brand-thanks-email]');
      done.hidden = false;
      done.focus();
    });
  };

  /* ---- start ---- */

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-product-reviews]'), function (root) {
      if (root.getAttribute('data-pr-ready')) return;
      root.setAttribute('data-pr-ready', 'true');
      new Reviews(root);
    });
  }

  function start() {
    init(document);
    // Theme editor: a re-rendered section is new markup that needs wiring again.
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }

  return {
    SORTS: SORTS,
    DEFAULT_SORT: DEFAULT_SORT,
    TOPIC_MIN: TOPIC_MIN,
    sortReviews: sortReviews,
    matchesSearch: matchesSearch,
    filterReviews: filterReviews,
    topicsToShow: topicsToShow,
    pageCount: pageCount,
    paginate: paginate,
    loadMore: loadMore,
    formatDate: formatDate,
    validateReview: validateReview,
    validateBrand: validateBrand,
    interpretResponse: interpretResponse,
    ERROR_CODES: ERROR_CODES,
    reviewNotice: reviewNotice,
    wantsForm: wantsForm,
    start: start
  };
});
